import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { SessionUser, SignedInSession, ViewingAs } from './types';

// Only who is signed in: details change on their own, and screens that read only the person must not re-render for them.
const SignedInUserContext = createContext<SessionUser | null>(null);

const ViewingAsContext = createContext<ViewingAs | null>(null);

const LandingPageContext = createContext<string | null>(null);

interface SignedInSessionProviderProps {
  readonly session: SignedInSession;
  readonly children: ReactNode;
}

/** Signed-in routes are opened for one person; every screen under them reads that person from here. */
export const SignedInSessionProvider = ({ session, children }: SignedInSessionProviderProps) => (
  <SignedInUserContext.Provider value={session.user}>
    <ViewingAsContext.Provider value={session.viewingAs ?? null}>
      <LandingPageContext.Provider value={session.landingPage ?? null}>{children}</LandingPageContext.Provider>
    </ViewingAsContext.Provider>
  </SignedInUserContext.Provider>
);

export const useViewingAs = (): ViewingAs | null => useContext(ViewingAsContext);

export const useSessionLandingPage = (): string | null => useContext(LandingPageContext);

export const useSignedInUser = (): SessionUser => {
  const user = useContext(SignedInUserContext);
  if (user === null) throw new Error('useSignedInUser needs a signed-in route: render the screen under AuthenticatedRoute.');
  return user;
};

/** Whether a record is the signed-in person's own. */
export const useIsSelf = (userId: string): boolean => {
  const { id } = useSignedInUser();
  return userId !== '' && userId === id;
};
